import React, { useCallback, useId, useState } from "react"
import { Repeater, types, useAdminContext } from "react-bricks/frontend"
import Typography, { TypographyElement } from "../../../../react-bricks/Typography"

import { Icon } from "../../../../react-bricks/components/icon"
import classNames from "classnames"
import { useMediaQuery } from "usehooks-ts"
import { Icons, pictogramOptions, Pictograms } from "../../../../types"
import { useCheckForEmptyText } from "../../../../hooks/use-check-for-empty-text"

interface TeaserAccordionProps {
    textVisible: TypographyElement[]
    textHidden: TypographyElement[]
    index?: number
    setIsOpen: (index?: number | ((isOpen: number) => number | undefined)) => void
    isOpen: number | undefined
    icon?: Pictograms
}

const TeaserAccordion: types.Brick<TeaserAccordionProps> = ({ index, setIsOpen, isOpen, textHidden, icon }) => {
    const [isOpenMobile, setIsOpenMobile] = useState(index === isOpen)

    const isMobile = useMediaQuery("(max-width: 767px")

    const open = useCallback(
        (index?: number) => (isMobile ? isOpenMobile : isOpen === index),
        [isMobile, isOpen, isOpenMobile]
    )

    const triggerId = useId()
    const contentId = useId()

    const emptyTextHidden = useCheckForEmptyText(textHidden)
    const { isAdmin } = useAdminContext()

    return (
        <li key={index}>
            <div className="grid grid-cols-[40px_auto] items-center gap-x-3.5 sm:grid-cols-[46px_auto] sm:gap-x-6">
                <Icon icon={icon!} className="text-[35px] sm:text-5xl sm:leading-none" />
                <button
                    type="button"
                    onClick={() => {
                        setIsOpen((isOpen: number) => (index === isOpen ? undefined : index))
                        setIsOpenMobile(isOpenMobile => !isOpenMobile)
                    }}
                    className="flex w-full justify-between gap-5 text-left font-gibson"
                    id={triggerId}
                    aria-expanded={open(index)}
                    aria-controls={contentId}
                >
                    <Typography propName="textVisible" allowList={["bold"]} block="copytext-faq-icon" />
                    <div>
                        <Icon
                            icon={Icons.arrow_down}
                            className={classNames("place-content-center text-md transition duration-300 ease-in-out ", {
                                "rotate-180": open(index),
                            })}
                        />
                    </div>
                </button>
                <div
                    className={classNames(
                        "col-start-2 grid transition-grid-template-rows duration-300 ease-out",
                        open(index) ? "grid-rows-1fr" : "grid-rows-0fr"
                    )}
                    id={contentId}
                    aria-labelledby={triggerId}
                    aria-hidden={!open(index)}
                >
                    <div className={classNames({ "overflow-hidden": !(isAdmin && open(index)) })}>
                        <div className="mt-3.5 space-y-7 pr-5 sm:mb-3">
                            {!emptyTextHidden && (
                                <Typography
                                    allowList={["bold", "sup", "link", "small", "checkList", "ul"]}
                                    propName="textHidden"
                                    hidden={!open(index)}
                                    block="copytext-faq-icon-small"
                                />
                            )}
                            <Repeater propName="repeater" itemProps={{ inline: true }} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="my-5 sm:my-7">
                <div className="h-px w-full bg-border-tide opacity-15" />
            </div>
        </li>
    )
}

TeaserAccordion.schema = {
    name: "TeaserAccordion",
    label: "TeaserAccordion",
    hideFromAddMenu: true,
    repeaterItems: [
        {
            name: "repeater",
            items: [
                {
                    type: "Table",
                    label: "Table",
                    max: 1,
                },
            ],
        },
    ],
    sideEditProps: [
        {
            name: "icon",
            label: "Icon",
            type: types.SideEditPropType.Select,
            selectOptions: {
                options: pictogramOptions,
                display: types.OptionsDisplay.Select,
            },
        },
    ],
    getDefaultProps: (): Omit<TeaserAccordionProps, "setIsOpen" | "isOpen" | "id"> => ({
        icon: Pictograms.vitality,
        textVisible: [
            {
                type: "paragraph",
                children: [
                    {
                        text: "Lorem ipsum dolor sit amet.",
                        bold: true,
                    },
                ],
            },
        ],
        textHidden: [
            {
                type: "paragraph",
                children: [
                    {
                        text: "Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet.",
                    },
                ],
            },
        ],
    }),
}

export default TeaserAccordion
